import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';

import { DropDownModule } from '@bayclubs/drop-down';

//application components
import {
	BcEmployeeAuthEnvironment,
	BcEmployeeAuthModule
} from '@bayclubs/employee-auth';
import { ApplicationModelV2, ApplicationsService, BASE_PATH } from "@bayclubs/employee-management-api";
import {
	AppVersionFooter,
	HorizonLoggingConfig,
	Toast,
	provideApplicationGroupsLoader,
	provideHorizonErrorHandling,
	provideHorizonLogging,
	provideHorizonLoginFactory,
	provideHorizonUserFactory,
	provideMenubarLogoHref,
	providePrimeng,
	provideVersionCheck,
	provideAuth,
	AuthService,
	provideHorizonErrorHandlingTesting,
	provideHorizonObservability,
	provideHorizonObservabilityPanel,
	createObservabilityTestPanelConfig,
	isDeployedRuntimeEnvironment
} from '@bayclubs/horizon';
import { SpinnerModule } from '@bayclubs/spinner';
import { LoaderInterceptor, TranslationService, UtilsModule } from '@bayclubs/utils';

import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsModule } from '@ngxs/store';

//application modules
import { ClipboardModule } from 'ngx-clipboard';
import { provideNgxMask } from 'ngx-mask';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { InfrastructureModule } from './infrastructure/infrastructure.module';
import { MenubarComponent } from "./shared/components/menubar/menubar.component";
import { SharedModule } from './shared/shared.module';

const legacyLoggingConfig = {
	consoleLogEnabled: false,
	seqLoggingEndpoint: environment.seqLoggingEndpoint,
	seqLoggingApiKey: environment.seqLoggingApiKey,
	appVersion: environment.appVersion,
	apiBaseUrl: environment.apiBaseUrl,
};

const horizonLoggingConfig: Partial<HorizonLoggingConfig> = {
	appVersion: environment.appVersion,
	console: {
		enabled: !environment.production,
	},
	seq: {
		enabled: !!environment.seqLoggingEndpoint,
		endpoint: environment.seqLoggingEndpoint,
		apiKey: environment.seqLoggingApiKey,
	},
	observability: {
		enabled: environment.observabilityEnabled === 'true',
		endpoint: environment.environment
			? `https://faro.${ environment.environment === 'prod' ? '' : `${ environment.environment }.` }bayclubs.io/collect`
			: undefined,
		serviceName: environment.observabilityServiceName,
		serviceNamespace: environment.observabilityServiceNamespace,
		environment: environment.environment,
	},
};

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [BrowserModule,
		BrowserAnimationsModule,
		AppRoutingModule,
		FormsModule,
		ReactiveFormsModule,
		NgxsModule.forRoot([], { developmentMode: !environment.production }),
		NgxsRouterPluginModule.forRoot(),
		NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
		BcEmployeeAuthModule.forRoot({
			accessDeniedRoute: '/forbidden',
			loginRoute: '/login',
			adClientId: environment.adClientId,
			adTenant: environment.adTenant,
			authenticationBaseUrl: environment.authenticationBaseUrl,
			baseHref: environment.baseHref
		} as BcEmployeeAuthEnvironment),
		UtilsModule.forRoot(),
		SharedModule.forRoot(),
		InfrastructureModule.forRoot(),
		SpinnerModule.forRoot(),
		DropDownModule.forRoot(),
		ClipboardModule,
		AppVersionFooter, MenubarComponent, Toast], providers: [
        { provide: 'AuthEnvironment', useValue: environment },
        { provide: 'loggingConfig', useValue: legacyLoggingConfig },
        provideAppInitializer(() => {
        const initializerFn = (initTranslations)(inject(TranslationService));
        return initializerFn();
      }),
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
		provideAuth({
			config: {
				clientId: environment.adClientId,
				tenantId: environment.adTenant,
				redirectUri: environment.adRedirectUri,
				protectedResources: {
					[environment.apiBaseUrl + '*']: environment.officeApiScope,
					[environment.employeeApiUrl + '/*']: environment.employeeApiScope
				},
				loginFailedRoute: '/login',
				returnUrlStrategy: 'remember'
			}
		}),
		{ provide: BASE_PATH, useValue: environment.employeeApiUrl },
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: 'localization/',
                suffix: `.json?timestamp=${new Date().getTime()}`,
                failOnError: true
            })
        }),
        provideHttpClient(withInterceptorsFromDi()),
        provideNgxMask(),
		provideServiceWorker('ngsw-worker.js', {
			enabled: isDeployedRuntimeEnvironment()
		}),
		providePrimeng(),
		provideVersionCheck(),
		provideMenubarLogoHref(environment.officeAppUrl),
		provideApplicationGroupsLoader<ApplicationModelV2>({
			loaderFactory: () => {
				const api = inject(ApplicationsService);
				return () => api.api10ApplicationsV2Get();
			},
			autoInit: false
		}),
		provideHorizonLogging({
			config: horizonLoggingConfig,
		}),
		provideHorizonUserFactory((authService) => {
			const auth = authService as AuthService;
			const account = auth.user();
			return account ? {
				id: account.localAccountId,
				name: account.name ?? account.username,
				login: account.username,
				email: account.username
			} : null;
		}, [AuthService]),
		provideHorizonErrorHandling(),
		provideHorizonErrorHandlingTesting({
			enabled: !environment.production
		}),
		provideHorizonLoginFactory((authService) => {
			const auth = authService as AuthService;
			return {
				isAuthenticated: () => auth.isAuthenticated(),
				login: () => auth.loginRedirect(),
			};
		}, [AuthService]),
		provideHorizonObservability(),
		provideHorizonObservabilityPanel(createObservabilityTestPanelConfig({
			apiBaseUrl: environment.apiBaseUrl,
			title: 'OMS Office Observability'
		}))
    ] })
export class AppModule {}

export function initTranslations(translationService: TranslationService) {
    return translationService.initSynchronously('locale-en');
}
